import { useEffect, useState, type FormEvent } from 'react';
import styled from 'styled-components';
import {
  useGetProgressObjectProfileQuery,
  useGetProgressScopeOptionsQuery,
  useSaveProgressObjectProfileMutation,
} from '@shared/api';
import { apiErrorMessage, fmtMonth, fmtRub, type ObjectProfileView } from '@entities/progress';
import { Modal } from '@shared/ui/Modal';
import { Button } from '@shared/ui/Button';
import { ChipsInput } from '@shared/ui/ChipsInput';
import { Field, Input, Label, Select } from '@shared/ui/Input';

/* «Привязки объекта» — чем объект связан с источниками без ключа объекта:
   почта Диспетчера (домены Заказчика + маркеры объекта), претензии Юриста
   (названия Заказчика), ленты ПА Сметчика, смета и серия совещаний. */

const Hint = styled.div`
  font-size: 12px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.text.muted};
  margin-top: ${({ theme }) => theme.spacing(1)};
`;

const Group = styled.fieldset`
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  padding: ${({ theme }) => `${theme.spacing(3)} ${theme.spacing(4)} ${theme.spacing(4)}`};
  margin: 0 0 ${({ theme }) => theme.spacing(4)};
  min-width: 0;

  & > legend {
    padding: 0 ${({ theme }) => theme.spacing(1.5)};
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: ${({ theme }) => theme.colors.text.secondary};
  }
`;

const RadioRow = styled.label`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  font-size: 13px;
  cursor: pointer;
  margin-bottom: ${({ theme }) => theme.spacing(1.5)};
`;

const LedgerList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1)};
  max-height: 220px;
  overflow: auto;
  margin-top: ${({ theme }) => theme.spacing(2)};
`;

const LedgerRow = styled.label<{ $disabled: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing(2)};
  padding: ${({ theme }) => `${theme.spacing(1.5)} ${theme.spacing(2)}`};
  border-radius: ${({ theme }) => theme.radius.sm};
  background: ${({ theme }) => theme.colors.surface};
  font-size: 12.5px;
  cursor: ${({ $disabled }) => ($disabled ? 'default' : 'pointer')};
  opacity: ${({ $disabled }) => ($disabled ? 0.75 : 1)};

  & small {
    display: block;
    color: ${({ theme }) => theme.colors.text.muted};
  }
`;

const AutoNote = styled.div`
  font-size: 12.5px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.text.secondary};
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  padding: ${({ theme }) => `${theme.spacing(2.5)} ${theme.spacing(3)}`};
  margin-bottom: ${({ theme }) => theme.spacing(4)};
`;

const Err = styled.div`
  color: ${({ theme }) => theme.colors.status.danger};
  font-size: 13px;
  margin-bottom: ${({ theme }) => theme.spacing(3)};
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing(2)};
`;

interface Props {
  projectId: string;
  objectName: string;
  canWrite: boolean;
  onClose: () => void;
}

interface FormState {
  customerName: string;
  domains: string[];
  aliases: string[];
  markers: string[];
  ledgersAuto: boolean;
  ledgerIds: string[];
  /** Площадки Хронума подбираются по названию объекта. */
  chronumAuto: boolean;
  chronumSiteIds: string[];
  estimateId: string;
  seriesId: string;
}

/** Заданное руководителем — как есть; пустые поля — из автоподбора (ПТО и
 *  Диспетчер), так форма сразу показывает то, что учитывается в цифрах. */
function initialState(view: ObjectProfileView): FormState {
  const p = view.profile;
  const a = view.auto;
  const list = (own: string[] | undefined, auto: string[]) => (own?.length ? own : auto);
  return {
    customerName: p?.customer_name ?? a.customer_name ?? '',
    domains: list(p?.customer_domains, a.customer_domains),
    aliases: list(p?.customer_aliases, a.customer_aliases),
    markers: list(p?.letter_markers, a.letter_markers),
    ledgersAuto: view.ledgers_auto,
    ledgerIds: view.ledgers.filter((l) => l.selected).map((l) => l.id),
    chronumAuto: view.chronum_sites_auto || !p?.chronum_site_ids?.length,
    chronumSiteIds: view.chronum_sites.filter((site) => site.selected).map((site) => site.id),
    estimateId: p?.contract_estimate_id ?? a.contract_estimate_id ?? '',
    seriesId: p?.meetings_series_id ?? '',
  };
}

/** Какие поля формы заполнены автоподбором (для подсказки). */
function autoFilledFields(view: ObjectProfileView): string[] {
  const p = view.profile;
  const a = view.auto;
  const out: string[] = [];
  if (!p?.customer_name && a.customer_name) out.push('Заказчик');
  if (!p?.customer_domains?.length && a.customer_domains.length) out.push('домены почты');
  if (!p?.customer_aliases?.length && a.customer_aliases.length) out.push('названия для претензий');
  if (!p?.letter_markers?.length && a.letter_markers.length) out.push('маркеры писем');
  if (!p?.contract_estimate_id && a.contract_estimate_id) out.push('смета договора');
  if (!p?.chronum_site_ids?.length && view.chronum_sites.some((site) => site.auto))
    out.push('площадки Хронума');
  return out;
}

export function ObjectProfileModal({ projectId, objectName, canWrite, onClose }: Props) {
  const { data: view, isLoading, isError } = useGetProgressObjectProfileQuery(projectId);
  const { data: options } = useGetProgressScopeOptionsQuery();
  const [save, { isLoading: saving }] = useSaveProgressObjectProfileMutation();
  const [form, setForm] = useState<FormState | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (view && !form) setForm(initialState(view));
  }, [view, form]);

  function patch(next: Partial<FormState>) {
    setForm((f) => (f ? { ...f, ...next } : f));
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!form) return;
    setError(null);
    try {
      await save({
        id: projectId,
        body: {
          customer_name: form.customerName.trim() || null,
          customer_domains: form.domains,
          customer_aliases: form.aliases,
          letter_markers: form.markers,
          accum_ledger_ids: form.ledgersAuto ? null : form.ledgerIds,
          contract_estimate_id: form.estimateId || null,
          meetings_series_id: form.seriesId || null,
          // Пусто — площадки подбираются по названию и коду объекта.
          chronum_site_ids: form.chronumAuto ? [] : form.chronumSiteIds,
        },
      }).unwrap();
      onClose();
    } catch (err) {
      setError(apiErrorMessage(err) ?? 'Не удалось сохранить привязки. Попробуйте ещё раз.');
    }
  }

  const disabled = !canWrite || saving;
  const autoIds = new Set(view?.ledgers.filter((l) => l.auto).map((l) => l.id) ?? []);
  const autoSiteIds = new Set(view?.chronum_sites.filter((s) => s.auto).map((s) => s.id) ?? []);

  return (
    <Modal open title={`Привязки объекта · ${objectName}`} onClose={onClose}>
      {isLoading || (!form && !isError) ? (
        <Hint>Загрузка…</Hint>
      ) : isError || !view || !form ? (
        <Err role="alert">Не удалось загрузить привязки объекта.</Err>
      ) : (
        <form onSubmit={handleSubmit}>
          {autoFilledFields(view).length > 0 && (
            <AutoNote>
              Подобрано автоматически по ПТО и почте Диспетчера: {autoFilledFields(view).join(', ')}
              . Проверьте и при необходимости поправьте — после сохранения значения закрепятся.
            </AutoNote>
          )}
          <Group>
            <legend>Заказчик</legend>
            <Field>
              <Label htmlFor="obj-customer">Наименование</Label>
              <Input
                id="obj-customer"
                value={form.customerName}
                onChange={(e) => patch({ customerName: e.target.value })}
                placeholder="Напр.: ООО «ЗапСибНефтехим»"
                maxLength={200}
                disabled={disabled}
              />
            </Field>
            <Field>
              <Label htmlFor="obj-domains">Почтовые домены Заказчика</Label>
              <ChipsInput
                id="obj-domains"
                value={form.domains}
                onChange={(domains) => patch({ domains })}
                placeholder="sibur.ru, polyus.com — Enter после каждого"
                maxItems={20}
                maxLength={253}
                disabled={disabled}
              />
              <Hint>
                Письма с этих доменов и их поддоменов считаются письмами Заказчика, письма на них —
                письмами Заказчику. Можно вставить адрес целиком — останется домен.
              </Hint>
            </Field>
            <Field>
              <Label htmlFor="obj-aliases">Названия Заказчика для претензий</Label>
              <ChipsInput
                id="obj-aliases"
                value={form.aliases}
                onChange={(aliases) => patch({ aliases })}
                placeholder="ЗапСибНефтехим, СИБУР, SIBUR"
                maxItems={20}
                maxLength={200}
                disabled={disabled}
              />
              <Hint>
                Претензия относится к объекту, если такое название встречается в контрагенте или
                претензия привязана к договору объекта.
              </Hint>
            </Field>
          </Group>

          <Group>
            <legend>Письма по объекту</legend>
            <Field>
              <Label htmlFor="obj-markers">Маркеры объекта в письмах</Label>
              <ChipsInput
                id="obj-markers"
                value={form.markers}
                onChange={(markers) => patch({ markers })}
                placeholder="БОВ, ДГП-2"
                maxItems={30}
                maxLength={200}
                disabled={disabled}
              />
              <Hint>
                Нужны, если у Заказчика несколько объектов: письмо засчитывается объекту, когда в
                теме или тексте есть маркер отдельным словом. Пусто — все письма Заказчика относятся
                к этому объекту.
                {view.contract_numbers.length > 0 &&
                  ` Номера договоров объекта добавляются к маркерам сами: ${view.contract_numbers.join(', ')}.`}
              </Hint>
            </Field>
          </Group>

          <Group>
            <legend>Процентовка (ленты ПА Сметчика)</legend>
            <RadioRow>
              <input
                type="radio"
                name="ledgers-mode"
                checked={form.ledgersAuto}
                onChange={() => patch({ ledgersAuto: true, ledgerIds: Array.from(autoIds) })}
                disabled={disabled}
              />
              Подбирать по номерам договоров объекта
            </RadioRow>
            <RadioRow>
              <input
                type="radio"
                name="ledgers-mode"
                checked={!form.ledgersAuto}
                onChange={() => patch({ ledgersAuto: false })}
                disabled={disabled}
              />
              Выбрать вручную
            </RadioRow>
            {view.ledgers.length === 0 ? (
              <Hint>У Сметчика пока нет лент ПА.</Hint>
            ) : (
              <LedgerList>
                {view.ledgers.map((l) => {
                  const checked = form.ledgersAuto
                    ? autoIds.has(l.id)
                    : form.ledgerIds.includes(l.id);
                  return (
                    <LedgerRow key={l.id} $disabled={form.ledgersAuto || disabled}>
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={form.ledgersAuto || disabled}
                        onChange={(e) =>
                          patch({
                            ledgerIds: e.target.checked
                              ? [...form.ledgerIds, l.id]
                              : form.ledgerIds.filter((x) => x !== l.id),
                          })
                        }
                      />
                      <span>
                        {l.contract_no} · {l.title || l.object_name}
                        <small>
                          {fmtRub(l.total_cost)}
                          {l.last_period ? ` · последний месяц ${fmtMonth(l.last_period)}` : ''}
                          {l.auto ? ' · совпадает номер договора' : ''}
                        </small>
                      </span>
                    </LedgerRow>
                  );
                })}
              </LedgerList>
            )}
          </Group>

          <Group>
            <legend>Люди на объекте (Хронум)</legend>
            {view.chronum_sites.length === 0 ? (
              <Hint>
                Площадки недоступны: Хронум не подключён к ОСНОВЕ или не ответил. Блок «Люди на
                объекте» на дашборде скажет об этом прямо.
              </Hint>
            ) : (
              <>
                <RadioRow>
                  <input
                    type="radio"
                    name="chronum-mode"
                    checked={form.chronumAuto}
                    onChange={() =>
                      patch({ chronumAuto: true, chronumSiteIds: Array.from(autoSiteIds) })
                    }
                    disabled={disabled}
                  />
                  Подбирать по названию и коду объекта
                </RadioRow>
                <RadioRow>
                  <input
                    type="radio"
                    name="chronum-mode"
                    checked={!form.chronumAuto}
                    onChange={() => patch({ chronumAuto: false })}
                    disabled={disabled}
                  />
                  Выбрать вручную
                </RadioRow>
                <LedgerList>
                  {view.chronum_sites.map((site) => {
                    const checked = form.chronumAuto
                      ? autoSiteIds.has(site.id)
                      : form.chronumSiteIds.includes(site.id);
                    return (
                      <LedgerRow key={site.id} $disabled={form.chronumAuto || disabled}>
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={form.chronumAuto || disabled}
                          onChange={(e) =>
                            patch({
                              chronumSiteIds: e.target.checked
                                ? [...form.chronumSiteIds, site.id]
                                : form.chronumSiteIds.filter((x) => x !== site.id),
                            })
                          }
                        />
                        <span>
                          {site.name}
                          <small>
                            на смене {site.on_shift_now} · закреплено {site.workers_attached}
                            {site.companies.length > 0 ? ` · ${site.companies.join(', ')}` : ''}
                            {site.auto ? ' · совпадает название' : ''}
                          </small>
                        </span>
                      </LedgerRow>
                    );
                  })}
                </LedgerList>
                <Hint>
                  Присутствие считает Хронум по отметкам на терминалах: забытые уходы в число людей
                  на смене не попадают. Если площадка подобрана неверно — выберите её вручную.
                </Hint>
              </>
            )}
          </Group>

          <Group>
            <legend>Сводный отчёт и прочие показатели</legend>
            <Field>
              <Label htmlFor="obj-estimate">Смета договора (освоение по КС-3)</Label>
              <Select
                id="obj-estimate"
                value={form.estimateId}
                onChange={(e) => patch({ estimateId: e.target.value })}
                disabled={disabled}
              >
                <option value="">— не привязывать —</option>
                {(options?.estimates ?? []).map((it) => (
                  <option key={it.id} value={it.id}>
                    {it.title}
                  </option>
                ))}
              </Select>
            </Field>
            <Field>
              <Label htmlFor="obj-series">Серия совещаний (поручения)</Label>
              <Select
                id="obj-series"
                value={form.seriesId}
                onChange={(e) => patch({ seriesId: e.target.value })}
                disabled={disabled}
              >
                <option value="">— не привязывать —</option>
                {(options?.series ?? []).map((it) => (
                  <option key={it.id} value={it.id}>
                    {it.title}
                  </option>
                ))}
              </Select>
            </Field>
          </Group>

          {error && <Err role="alert">{error}</Err>}
          <Actions>
            <Button type="button" $variant="ghost" onClick={onClose}>
              {canWrite ? 'Отмена' : 'Закрыть'}
            </Button>
            {canWrite && (
              <Button type="submit" $variant="primary" disabled={saving}>
                {saving ? 'Сохраняю…' : 'Сохранить'}
              </Button>
            )}
          </Actions>
        </form>
      )}
    </Modal>
  );
}
